import { onBeforeUnmount, ref, Ref, watch } from 'vue';

const MOVE_THRESHOLD = 8;
const TOP_ZONE = 8;

export const useHideOnScroll = (
  scrollContainer: Ref<HTMLElement | Window | null | undefined>,
) => {
  const isHidden = ref(false);
  let lastScrollY = 0;
  let attachedTo: HTMLElement | Window | undefined;

  const getScrollY = (target: HTMLElement | Window): number =>
    target instanceof Window ? target.scrollY : target.scrollTop;

  const onScroll = () => {
    if (!attachedTo) return;
    const current = getScrollY(attachedTo);
    const delta = current - lastScrollY;

    if (current <= TOP_ZONE) {
      isHidden.value = false;
      lastScrollY = current;
      return;
    }

    if (Math.abs(delta) < MOVE_THRESHOLD) return;

    isHidden.value = delta > 0;
    lastScrollY = current;
  };

  const detach = () => {
    attachedTo?.removeEventListener('scroll', onScroll);
    attachedTo = undefined;
  };

  watch(
    scrollContainer,
    target => {
      detach();
      if (!target) return;
      attachedTo = target;
      lastScrollY = getScrollY(target);
      target.addEventListener('scroll', onScroll, { passive: true });
    },
    { immediate: true },
  );

  onBeforeUnmount(detach);

  const reveal = () => {
    isHidden.value = false;
    if (attachedTo) lastScrollY = getScrollY(attachedTo);
  };

  return { isHidden, reveal };
};
